import React, { useEffect, useRef } from 'react';

// A shared empty set for defaults: a fresh `new Set()` per render is a new
// dependency for the rebuild below, which then redraws every node on any
// render — including each zoom tick.
const NO_IDS = new Set();
import * as d3 from 'd3';
import { renderImpactPathNode } from './renderImpactPathNode';
import { renderImpactPathStartNode } from './ImpactPathStartNode';
import { renderGoalNode } from './D3NodeRenderer';
import { useAiUnread } from '../../contexts/AiUnreadContext';
import { hasNodeAiAnalysis } from '../../utils/aiAnalysisPresence';
import { computeUnreadDotZoomScale, updateAiUnreadDotZoom } from './appendAiUnreadDot';

/**
 * PathwayNodeLayer - Renders all nodes with D3
 *
 * Takes positions and node data, renders them using existing D3 renderers.
 * Handles click events, double-click zoom, and drag behavior.
 */
export default function PathwayNodeLayer({
  nodes,
  positions,
  onNodeClick,
  onNodeDrag,
  onAddConsequenceClick, // New prop for (+) button clicks
  // The map's working mode decides whether adding a consequence is on the
  // table at all (config/impactMapModes.js). False = no (+) anywhere.
  showAddButton = true,
  // Ids to keep lit while something is selected; null means nothing is.
  litNodeIds = null,
  currentParticipantId, // Current user's participant ID for highlighting own nodes
  highlightedNodeId, // Node to highlight (e.g., parent when adding consequence)
  choicePointIds = null,    // Set of node ids that fork into wanted and unwanted
  processingNodeIds = NO_IDS, // Nodes currently being AI-processed (show glimmer)
  pulsingNodeId = null, // Node in active edit / add flow (pulse ring)
  freshHighlightNodeIds = NO_IDS, // Newly created nodes (brief glow)
  nodeTypeLabels = null,
  zoomK = 1,
}) {
  const groupRef = useRef();
  const clickTimerRef = useRef(null);
  const { isUnread } = useAiUnread();
  // The unread dot keeps its screen size across zoom. The rebuild below reads
  // the scale through a ref so that zooming does not re-run it — with the
  // scale in its deps every wheel tick tore down and redrew every node on the
  // map. The light effect under it rescales the dots that already exist.
  const zoomDotScaleRef = useRef(computeUnreadDotZoomScale(zoomK));
  zoomDotScaleRef.current = computeUnreadDotZoomScale(zoomK);

  useEffect(() => {
    if (!groupRef.current) return;
    updateAiUnreadDotZoom(d3.select(groupRef.current), zoomK);
  }, [zoomK]);

  useEffect(() => {
    if (!groupRef.current || !nodes || !positions) return;

    const container = d3.select(groupRef.current);
    container.selectAll('*').remove();

    // Add SVG filter for highlight glow effect
    const defs = container.append('defs');
    const filter = defs.append('filter')
      .attr('id', 'highlight-glow')
      .attr('x', '-50%')
      .attr('y', '-50%')
      .attr('width', '200%')
      .attr('height', '200%');

    filter.append('feGaussianBlur')
      .attr('in', 'SourceGraphic')
      .attr('stdDeviation', '8')
      .attr('result', 'blur');

    filter.append('feFlood')
      .attr('flood-color', '#5B9DD9')
      .attr('flood-opacity', '0.8')
      .attr('result', 'color');

    filter.append('feComposite')
      .attr('in', 'color')
      .attr('in2', 'blur')
      .attr('operator', 'in')
      .attr('result', 'glow');

    const feMerge = filter.append('feMerge');
    feMerge.append('feMergeNode').attr('in', 'glow');
    feMerge.append('feMergeNode').attr('in', 'glow');
    feMerge.append('feMergeNode').attr('in', 'SourceGraphic');

    // Create node groups
    const nodeGroups = container
      .selectAll('.node')
      .data(nodes)
      .enter()
      .append('g')
      .attr('class', d => highlightedNodeId === d.id ? 'node highlighted' : 'node')
      .attr('transform', d => {
        const pos = positions[d.id];
        return pos ? `translate(${pos.x}, ${pos.y})` : 'translate(0, 0)';
      })
      .attr('filter', d => highlightedNodeId === d.id ? 'url(#highlight-glow)' : null)
      .style('cursor', 'pointer');

    // Render each node using appropriate renderer
    nodeGroups.each(function(d) {
      const group = d3.select(this);

      if (d.type === 'target' || d.type === 'goal') {
        renderGoalNode(group, d);
      } else {
        // Check if this node should be expanded (show description)
        const isExpanded = highlightedNodeId === d.id;
        // Everything that is not part of what is being looked at recedes.
        group.attr('opacity', litNodeIds && !litNodeIds.has(d.id) ? 0.09 : 1)
          .style('transition', 'opacity 0.18s ease');

        renderImpactPathNode(group, {
          title: d.title || d.text,
          mood: d.type === 'start' ? 'start' : d.mood,  // Use 'start' mood for start nodes
          categories: d.categories,
          category: d.category,
          depth: d.depth,
          isHuman: d.isHuman || d.type === 'human',
          type: d.type,
          reactions: d.reactions,
          members: d.members || [], // Pass members array for grouped nodes
          participantName: d.participantName, // Pass participant name for start nodes
          created_by: d.created_by, // Pass creator for label display (system or participant ID)
          // Drives the "Muuttuja" vs "Seuraus" label. pathwayDataTransform
          // already carries it; this layer builds an explicit prop object, so
          // omitting it here silently fell back to the default (false) and
          // user-created muuttujat kept reading "Seuraus".
          is_scenario_starter: d.is_scenario_starter,
          id: d.id, // Pass ID for split action
          isOwnNode: currentParticipantId && d.participantId === currentParticipantId, // Highlight user's own nodes
          // Colour of the edge that reaches this node (v2). This layer builds an
          // explicit prop object — anything not named here never reaches the
          // renderer, however faithfully it was set upstream.
          borderColor: d.borderColor,
          // Scenario accent for depth-0 roots (scenario.color or name lookup)
          scenarioColor: d.scenarioColor || d.color || null,
          color: d.color || null,
          // Valence layer. Same rule as borderColor above — named here or it
          // never reaches the renderer.
          valence: d.valence || null,
          isChoicePoint: Boolean(choicePointIds?.has(d.id)),
          isProcessing: processingNodeIds.has(d.id),
          hasUnreadAi: (() => {
            const isOwn = currentParticipantId && d.participantId === currentParticipantId;
            const analysis = d.node_analysis || null;
            return isOwn
              && !processingNodeIds.has(d.id)
              && isUnread('node', d.id, hasNodeAiAnalysis(analysis));
          })(),
          isPulsing: pulsingNodeId != null && pulsingNodeId === d.id,
          isFreshHighlight: freshHighlightNodeIds.has(d.id),
          node_analysis: d.node_analysis || null,
          // This layer builds an EXPLICIT prop object: anything not named here
          // never reaches the renderer, however faithfully it was computed.
          hasSuggestion: d.hasSuggestion === true,
          // Expanded state for showing description
          isExpanded: isExpanded,
          description: d.description,
          // Human/AI content separation
          human_text: d.human_text,
          ai_analysis: d.ai_analysis,
          ai_sources: d.ai_sources || [],
          nodeTypeLabels,
          zoomDotScale: zoomDotScaleRef.current,
          onClick: (eventData) => {
            // Only handle split actions here (member box clicks)
            // Regular node clicks are handled by nodeGroups handlers below
            if (eventData && eventData.action === 'split' && onNodeClick) {
              onNodeClick({ ...d, splitAction: eventData });
            }
          }
        });

        // Add (+) button to the right of the node
        if (showAddButton) {
          const cardWidth = 360;
          const cardHeight = 90;
          const buttonX = cardWidth / 2 + 36; // Increased gap from 28 to 36
          const buttonY = 0; // Center vertically

          const addButton = group.append('g')
          .attr('class', 'add-consequence-button')
          .attr('transform', `translate(${buttonX}, ${buttonY})`)
          .style('cursor', 'pointer')
          .on('click', function(event) {
            event.stopPropagation();
            console.log('➕ Add consequence button clicked for node:', d.id);

            // Get screen position of (+) button for inline modal positioning
            // The button is at buttonX, buttonY relative to the node group
            // We need to calculate the screen coordinates accounting for current zoom/pan
            const svgElement = groupRef.current?.closest('svg');
            let screenPosition = null;

            if (svgElement) {
              const point = svgElement.createSVGPoint();
              const pos = positions[d.id];
              // Button center in SVG coordinates
              point.x = pos.x + buttonX;
              point.y = pos.y + buttonY;

              // Get the CTM (current transformation matrix) to convert to screen coords
              const ctm = groupRef.current.getScreenCTM();
              if (ctm) {
                const screenPoint = point.matrixTransform(ctm);
                screenPosition = {
                  x: screenPoint.x,
                  y: screenPoint.y,
                  // Also pass SVG coordinates for potential canvas rendering
                  svgX: point.x,
                  svgY: point.y
                };
                console.log('📍 Button screen position:', screenPosition);
              }
            }

            // Use separate handler for (+) button if provided, otherwise fall back to onNodeClick
            const handler = onAddConsequenceClick || onNodeClick;
            if (handler) handler(d, screenPosition);
          });

        // Button circle (bigger: 22px radius instead of 18px)
        addButton.append('circle')
          .attr('r', 22)
          .attr('fill', '#0a0a0a')
          .attr('stroke', '#555')
          .attr('stroke-width', 1)
          .on('mouseenter', function() {
            d3.select(this)
              .attr('fill', '#5B9DD9')
              .attr('stroke', '#5B9DD9');
          })
          .on('mouseleave', function() {
            d3.select(this)
              .attr('fill', '#0a0a0a')
              .attr('stroke', '#555');
          });

        // Plus icon (vertical line) - bigger: 8px instead of 6px
        const verticalLine = addButton.append('line')
          .attr('x1', 0)
          .attr('y1', -8)
          .attr('x2', 0)
          .attr('y2', 8)
          .attr('stroke', '#888')
          .attr('stroke-width', 2)
          .attr('stroke-linecap', 'round')
          .style('pointer-events', 'none');

        // Plus icon (horizontal line) - bigger: 8px instead of 6px
        const horizontalLine = addButton.append('line')
          .attr('x1', -8)
          .attr('y1', 0)
          .attr('x2', 8)
          .attr('y2', 0)
          .attr('stroke', '#888')
          .attr('stroke-width', 2)
          .attr('stroke-linecap', 'round')
          .style('pointer-events', 'none');

          // Update plus icon color on hover
          addButton
            .on('mouseenter', function() {
              verticalLine.attr('stroke', '#fff');
              horizontalLine.attr('stroke', '#fff');
            })
            .on('mouseleave', function() {
              verticalLine.attr('stroke', '#888');
              horizontalLine.attr('stroke', '#888');
            });
        }
      }
    });

    // Add click and double-click handlers
    nodeGroups
      .on('dblclick', function(event, d) {
        event.stopPropagation();
        event.preventDefault();

        console.log('🖱️ Double-click detected on node:', d.id);

        // Cancel single-click timer if it exists
        if (clickTimerRef.current) {
          clearTimeout(clickTimerRef.current);
          clickTimerRef.current = null;
          console.log('✅ Canceled pending single-click');
        }

        // Find the SVG element by traversing up from the group
        let svgElement = groupRef.current;
        while (svgElement && svgElement.tagName !== 'svg') {
          svgElement = svgElement.parentElement;
        }

        if (!svgElement) {
          console.error('❌ Could not find SVG element');
          return;
        }

        const svg = d3.select(svgElement);
        const pos = positions[d.id];

        if (!pos) {
          console.warn('⚠️ No position found for node:', d.id);
          return;
        }

        // Get current transform
        const currentTransform = d3.zoomTransform(svgElement);

        // Calculate center position (account for card size)
        const cardWidth = 360;
        const cardHeight = 90;
        const nodeCenterX = pos.x + cardWidth / 2;
        const nodeCenterY = pos.y + cardHeight / 2;

        // Calculate new transform to center on node
        const svgWidth = svgElement.clientWidth;
        const svgHeight = svgElement.clientHeight;
        // Clamp newScale between scaleExtent limits (0.01 to 4)
        const targetScale = currentTransform.k * 1.5;
        const newScale = Math.max(0.01, Math.min(targetScale, 4)); // Enforce min 1%, max 400%
        const newX = svgWidth / 2 - nodeCenterX * newScale;
        const newY = svgHeight / 2 - nodeCenterY * newScale;

        console.log('🔍 Zooming to node:', {
          svgFound: !!svgElement,
          svgSize: { width: svgWidth, height: svgHeight },
          oldScale: currentTransform.k,
          newScale,
          nodeCenter: { x: nodeCenterX, y: nodeCenterY },
          newTransform: { x: newX, y: newY, k: newScale }
        });

        // Create the new transform
        const newTransform = d3.zoomIdentity
          .translate(newX, newY)
          .scale(newScale);

        console.log('🎯 Applying zoom transform:', newTransform);

        // Get the actual zoom behavior that was set up in PathwayCanvas
        const zoomBehavior = svgElement.__zoomBehavior;

        if (zoomBehavior) {
          console.log('✅ Found zoom behavior, applying transition');
          // Use the actual zoom behavior instance to apply the transform
          // This triggers the zoom event handler which updates the container transform
          svg.transition()
            .duration(750)
            .ease(d3.easeCubicOut)
            .call(zoomBehavior.transform, newTransform)
            .on('end', () => {
              console.log('✅ Zoom transition complete');
            });
        } else {
          console.error('❌ No zoom behavior found on SVG element');
        }
      })
      .on('click', function(event, d) {
        event.stopPropagation();

        console.log('🖱️ Click detected on node:', d.id);

        // Clear any existing timer
        if (clickTimerRef.current) {
          clearTimeout(clickTimerRef.current);
          clickTimerRef.current = null;
        }

        // Set a timer for single click (300ms delay to detect double-click)
        clickTimerRef.current = setTimeout(() => {
          console.log('✅ Single-click confirmed, opening modal for:', d.id);
          if (onNodeClick) onNodeClick(d);
          clickTimerRef.current = null;
        }, 300);
      });

    // Add drag behavior
    if (onNodeDrag) {
      nodeGroups.call(
        d3.drag()
          .on('start', function(event, d) {
            d3.select(this).classed('dragging', true);
            onNodeDrag('start', d, event);
          })
          .on('drag', function(event, d) {
            d3.select(this).attr('transform', `translate(${event.x}, ${event.y})`);
            onNodeDrag('drag', d, event);
          })
          .on('end', function(event, d) {
            d3.select(this).classed('dragging', false);
            onNodeDrag('end', d, event);
          })
      );
    }

    // Cleanup timer on unmount
    return () => {
      if (clickTimerRef.current) {
        clearTimeout(clickTimerRef.current);
      }
    };
  }, [nodes, positions, onNodeClick, onNodeDrag, onAddConsequenceClick, showAddButton, litNodeIds, currentParticipantId, highlightedNodeId, processingNodeIds, pulsingNodeId, freshHighlightNodeIds, nodeTypeLabels, isUnread, choicePointIds]);

  return <g ref={groupRef} className="nodes-layer" />;
}
